import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser';
import Layout from './pages/layout/Layout';
import LandingPage from './pages/landing/Landing';
import Dashboard from './pages/dashboard/Dashboard';
import ExploreDataPage from './pages/explore_data/ExploreData';
import ConfigurePage from './pages/configure/Configure';
import NotFoundPage from './pages/NotFoundPage';
import LoginPage from './pages/login/LoginPage';
import { PromptManagement } from './pages/PromptManagement';
import { GlossaryManagement } from './pages/GlossaryManagement';
import SuggestedQueriesManagement from './pages/SuggestedQueriesManagement';
import ConversationsPage from './pages/conversations/Conversations';
import { BrandProvider } from './providers/BrandProvider';
import { UserProvider, useUserContext } from './components/UserContext/UserContext';
// Import brand-specific favicon functions directly (same pattern as Logo)
import { setBrandAssets } from '@brand';
import { fetchUserProfile } from './api/api';
import './App.css';
import './styles/containers.css';
import msalConfig from '../src/api/authConfig';

// Initialize the MSAL instance outside the component to keep it persistent.
export const msalInstance = new PublicClientApplication(msalConfig);

const ProtectedRoute = ({ element }: { element: JSX.Element }) => {
    const isAuthenticated = useIsAuthenticated();
    const { inProgress } = useMsal();
    const { userProfile } = useUserContext();

    if (inProgress !== InteractionStatus.None) {
        return <div>Loading...</div>;
    }

    // Require both MSAL authentication AND a loaded user profile.
    // This prevents rendering pages when MSAL has a stale cached account
    // but the token hasn't been acquired yet (which causes 401s).
    return (isAuthenticated && userProfile) ? element : <Navigate to="/" />;
};

// Centralized Auth State Component
const AuthStateManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { userProfile, setUserProfile } = useUserContext();
    const isAuthenticated = useIsAuthenticated();
    const { inProgress, accounts } = useMsal();
    const [authLoading, setAuthLoading] = useState(true);

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                if (isAuthenticated && !userProfile && accounts.length > 0) {
                    // User is authenticated but profile not loaded
                    console.log("User is authenticated, fetching profile...");
                    const profile = await fetchUserProfile();
                    if (profile) {
                        setUserProfile(profile);
                    } else {
                        console.warn("Failed to fetch user profile, user may need to re-authenticate");
                        setUserProfile(null);
                    }
                } else if (!isAuthenticated && userProfile) {
                    // User is not authenticated but profile exists (logout case)
                    console.log("User logged out, clearing profile");
                    setUserProfile(null);
                } else if (!isAuthenticated && accounts.length === 0) {
                    // No accounts and not authenticated - this is normal for login page
                    console.log("No authenticated accounts found");
                    setUserProfile(null);
                }
            } catch (error) {
                console.error('Error initializing auth:', error);
                setUserProfile(null);
            } finally {
                setAuthLoading(false);
            }
        };

        if (inProgress === InteractionStatus.None) {
            initializeAuth();
        }
    }, [isAuthenticated, userProfile, setUserProfile, inProgress, accounts.length]);

    if (inProgress !== InteractionStatus.None || authLoading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                fontSize: '16px'
            }}>
                Loading...
            </div>
        );
    }

    return <>{children}</>;
};

const App: React.FC = () => {
    const [msalInitialized, setMsalInitialized] = useState(false);

    useEffect(() => {
        const initializeMsal = async () => {
            try {
                await msalInstance.initialize(); // Make sure to await initialization
                
                // Handle redirect response (for redirect authentication)
                try {
                    const response = await msalInstance.handleRedirectPromise();
                    if (response) {
                        console.log("User authenticated via redirect");
                    }
                } catch (redirectError) {
                    // No redirect response - this is normal on initial page load
                }
                
                setMsalInitialized(true); // Once initialized, set state to allow rendering
                
                // Set brand-specific favicon and title
                // Brand is resolved at build time via @brand alias (same pattern as Logo)
                setBrandAssets();
            } catch (error) {
                console.error('Error initializing MSAL:', error);
            }
        };

        initializeMsal(); // Call the function on component mount
    }, []);

    if (!msalInitialized) {
        return <div>Loading...</div>;
    }

    return (
        <BrandProvider>
            <MsalProvider instance={msalInstance}>
                <UserProvider>
                    <AuthStateManager>
                        <Router>
                            <Routes>
                                <Route path="/" element={<Layout />}>
                                    <Route index element={<LoginPage />} />
                                    <Route path="home" element={<ProtectedRoute element={<LandingPage />} />} />
                                    <Route path="dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
                                    <Route path="conversations" element={<ProtectedRoute element={<ConversationsPage />} />} />
                                    <Route path="explore" element={<ProtectedRoute element={<ExploreDataPage />} />} />
                                    <Route path="configure" element={<ProtectedRoute element={<ConfigurePage />} />} />
                                    <Route path="prompts" element={<ProtectedRoute element={<PromptManagement />} />} />
                                    <Route path="glossary" element={<ProtectedRoute element={<GlossaryManagement />} />} />
                                    <Route path="suggested-queries" element={<ProtectedRoute element={<SuggestedQueriesManagement />} />} />
                                </Route>
                                
                                <Route path="*" element={<NotFoundPage />} />
                            </Routes>
                        </Router>
                    </AuthStateManager>
                </UserProvider>
            </MsalProvider>
        </BrandProvider>
    );
};

export default App;
